import { useState, useCallback, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  adminMembersService,
  type Member,
  type Invitation,
} from "@/services/adminMembersService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import {
  Users,
  UserPlus,
  Mail,
  MoreVertical,
  Clock,
  Loader2,
  X,
  Send,
  UserMinus,
} from "lucide-react";
import { format } from "date-fns";

interface MembersTabProps {
  organizationId: string;
  organizationName: string;
}

export function MembersTab({
  organizationId,
  organizationName,
}: MembersTabProps) {
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [isInviting, setIsInviting] = useState(false);
  const [selectedTab, setSelectedTab] = useState("members");

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // TanStack Query: Fetch members and invitations
  const {
    data: membersData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["admin-members", organizationId],
    queryFn: () =>
      adminMembersService.getMembers({
        organizationId,
        page: 1,
        pageSize: 100,
      }),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // TanStack Query: Invite member mutation
  const inviteMemberMutation = useMutation({
    mutationFn: ({
      organizationId,
      email,
    }: {
      organizationId: string;
      email: string;
    }) => adminMembersService.inviteMember({ organizationId, email }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-members", organizationId],
      });
      toast({
        title: "Invitation sent",
        description: `An invitation has been sent to ${inviteEmail}`,
      });
      setInviteEmail("");
      setIsInviteDialogOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // TanStack Query: Remove member mutation
  const removeMemberMutation = useMutation({
    mutationFn: ({ memberId }: { memberId: string }) =>
      adminMembersService.removeMember(organizationId, memberId, true), // Always delete auth user (no multi-tenancy)
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["admin-members", organizationId],
      });
      toast({
        title: "Member removed",
        description: data.warning
          ? `Member removed from organization. ${data.warning}`
          : "The member and their account have been completely deleted.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // TanStack Query: Cancel invitation mutation
  const cancelInvitationMutation = useMutation({
    mutationFn: ({ invitationId }: { invitationId: string }) =>
      adminMembersService.cancelInvitation(organizationId, invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-members", organizationId],
      });
      toast({
        title: "Invitation cancelled",
        description: "The invitation has been cancelled.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Event handlers
  const handleInviteMember = useCallback(() => {
    if (!inviteEmail.trim()) return;
    setIsInviting(true);
    inviteMemberMutation.mutate(
      { organizationId, email: inviteEmail.trim() },
      {
        onSettled: () => {
          setIsInviting(false);
        },
      },
    );
  }, [inviteEmail, organizationId, inviteMemberMutation]);

  const handleRemoveMember = useCallback(
    (memberId: string, memberEmail: string) => {
      if (
        confirm(
          `Are you sure you want to remove ${memberEmail} from this organization?\n\n⚠️ This will also DELETE their account completely. They will need to be re-invited to join again.`,
        )
      ) {
        removeMemberMutation.mutate({ memberId });
      }
    },
    [removeMemberMutation],
  );

  const handleCancelInvitation = useCallback(
    (invitationId: string, email: string) => {
      if (
        confirm(`Are you sure you want to cancel the invitation for ${email}?`)
      ) {
        cancelInvitationMutation.mutate({ invitationId });
      }
    },
    [cancelInvitationMutation],
  );

  // Derived data
  const members = useMemo(
    () => membersData?.members || [],
    [membersData?.members],
  );
  const invitations = useMemo(
    () => membersData?.invitations || [],
    [membersData?.invitations],
  );

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "MMM d, yyyy");
  };

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600">
          Failed to load members: {(error as Error).message}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with invite button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Team Members</h3>
          <p className="text-sm text-gray-600">
            Manage members and invitations for {organizationName}
          </p>
        </div>
        <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <UserPlus className="h-4 w-4 mr-2" />
              Invite Member
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Invite Team Member</DialogTitle>
              <DialogDescription>
                Send an invitation to join {organizationName}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="colleague@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && inviteEmail.trim()) {
                      handleInviteMember();
                    }
                  }}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsInviteDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleInviteMember}
                disabled={!inviteEmail.trim() || isInviting}
              >
                {isInviting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Invitation
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs for Members and Invitations */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="members">
            Active Members ({members.length})
          </TabsTrigger>
          <TabsTrigger value="invitations">
            Pending Invitations ({invitations.length})
          </TabsTrigger>
        </TabsList>

        {/* Active Members Tab */}
        <TabsContent value="members" className="mt-6">
          <ScrollArea className="h-[400px] border rounded-lg p-4">
            {isLoading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : members.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-20" />
                <p>No team members yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {members.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/5 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={member.avatar_url || undefined} />
                        <AvatarFallback>
                          {(member.full_name || member.email)
                            .charAt(0)
                            .toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">
                          {member.full_name || member.email}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {member.email}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Joined {formatDate(member.joined_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() =>
                              handleRemoveMember(member.id, member.email)
                            }
                          >
                            <UserMinus className="h-4 w-4 mr-2" />
                            Remove from team
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </TabsContent>

        {/* Pending Invitations Tab */}
        <TabsContent value="invitations" className="mt-6">
          <ScrollArea className="h-[400px] border rounded-lg p-4">
            {isLoading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : invitations.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Mail className="h-12 w-12 mx-auto mb-4 opacity-20" />
                <p>No pending invitations</p>
              </div>
            ) : (
              <div className="space-y-4">
                {invitations.map((invitation) => (
                  <div
                    key={invitation.id}
                    className="flex items-center justify-between p-4 rounded-lg border bg-card"
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                        <Mail className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="font-medium">{invitation.email}</p>
                        <p className="text-sm text-muted-foreground">
                          Invited on {formatDate(invitation.created_at)}
                        </p>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                          <Clock className="h-3 w-3" />
                          Expires {formatDate(invitation.expires_at)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() =>
                              handleCancelInvitation(
                                invitation.id,
                                invitation.email,
                              )
                            }
                          >
                            <X className="h-4 w-4 mr-2" />
                            Cancel invitation
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </TabsContent>
      </Tabs>
    </div>
  );
}
